import { useEffect, useMemo, useState } from 'react';
import type { SyntheticEvent } from 'react';
import type { AgencyGateway } from '../application/agencyGateway';
import type { AgencyBlogPost, AgencyContext, AgencyListing, AgencyRelationship, AgencyTeamMember } from '../domain/agency';
import type { ConversationMessage } from '../domain/workspaces';
import styles from './AgencyScreen.module.css';

interface AgencyScreenProps {
  readonly gateway: AgencyGateway;
  readonly navigateToAccess?: () => void;
  readonly navigateToDashboard?: () => void;
}

function defaultNavigateToAccess():void{window.location.replace('/');}
function defaultNavigateToDashboard():void{window.location.assign('/dashboard');}
function initials(name:string):string{return name.split(' ').filter(Boolean).slice(-2).map(part=>part.charAt(0)).join('').slice(0,2).toUpperCase();}
function money(value:number,currency:string):string{return new Intl.NumberFormat('es-MX',{style:'currency',currency,maximumFractionDigits:0}).format(value);}

const cover='https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1600&q=84';

export function AgencyScreen({gateway,navigateToAccess=defaultNavigateToAccess,navigateToDashboard=defaultNavigateToDashboard}:AgencyScreenProps):React.JSX.Element{
  const [identityName,setIdentityName]=useState<string|null>(null);
  const [context,setContext]=useState<AgencyContext|null>(null);
  const [team,setTeam]=useState<readonly AgencyTeamMember[]>([]);
  const [selectedAgentId,setSelectedAgentId]=useState<string|null>(null);
  const [listings,setListings]=useState<readonly AgencyListing[]>([]);
  const [relationships,setRelationships]=useState<readonly AgencyRelationship[]>([]);
  const [blogPosts,setBlogPosts]=useState<readonly AgencyBlogPost[]>([]);
  const [blogTitle,setBlogTitle]=useState('');
  const [blogSubtitle,setBlogSubtitle]=useState('');
  const [blogBody,setBlogBody]=useState('');
  const [blogEditingId,setBlogEditingId]=useState<string|null>(null);
  const [blogSaving,setBlogSaving]=useState(false);
  const [blogEditorOpen,setBlogEditorOpen]=useState(false);
  const [conversationId,setConversationId]=useState<string|null>(null);
  const [messages,setMessages]=useState<readonly ConversationMessage[]>([]);
  const [draft,setDraft]=useState('');
  const [loading,setLoading]=useState(true);
  const [loadingDetail,setLoadingDetail]=useState(false);
  const [openingDm,setOpeningDm]=useState(false);
  const [sending,setSending]=useState(false);
  const [error,setError]=useState<string|null>(null);
  const [section,setSection]=useState<'profile'|'inventory'|'blog'>('profile');

  const selected=useMemo(()=>team.find(member=>member.agentId===selectedAgentId)??null,[selectedAgentId,team]);
  const customers=relationships.filter(relationship=>relationship.relationshipType!=='owner');
  const owners=relationships.filter(relationship=>relationship.relationshipType==='owner');

  useEffect(()=>{
    const controller=new AbortController();
    void gateway.resolveCurrentIdentity(controller.signal).then(async identity=>{
      if(!identity.ok){navigateToAccess();return;}
      setIdentityName(identity.identity.displayName);
      const [contextResult,teamResult]=await Promise.all([gateway.loadContext(controller.signal),gateway.loadTeam(controller.signal)]);
      if(!contextResult.ok){
        setLoading(false);
        setContext(null);
        setTeam([]);
        setSelectedAgentId(null);
        setError(contextResult.message==='No fue posible resolver la identidad de Agency.'?null:contextResult.message);
        return;
      }
      if(!teamResult.ok){
        setLoading(false);
        setContext(contextResult.context);
        setError(teamResult.message);
        return;
      }
      if(teamResult.team.length===0){
        setLoading(false);
        setContext(contextResult.context);
        setTeam([]);
        setSelectedAgentId(null);
        setError(null);
        return;
      }
      const self=teamResult.team.find(member=>member.isSelf)??teamResult.team.at(0);
      if(self===undefined){setLoading(false);setError('Agency no tiene identidad profesional disponible.');return;}
      setContext(contextResult.context);setTeam(teamResult.team);setSelectedAgentId(self.agentId);setLoadingDetail(true);setLoading(false);setError(null);
    }).catch((cause:unknown)=>{if(!controller.signal.aborted&&!(cause instanceof DOMException&&cause.name==='AbortError'))navigateToAccess();});
    return()=>controller.abort();
  },[gateway,navigateToAccess]);

  useEffect(()=>{
    if(selectedAgentId===null)return;
    const controller=new AbortController();
    void Promise.all([
      gateway.loadInventory(selectedAgentId,controller.signal),
      gateway.loadRelationships(selectedAgentId,controller.signal),
      gateway.loadBlog(selectedAgentId,controller.signal),
    ]).then(([inventoryResult,relationshipsResult,blogResult])=>{
      if(!inventoryResult.ok){setError(inventoryResult.message);setLoadingDetail(false);return;}
      if(!relationshipsResult.ok){setError(relationshipsResult.message);setLoadingDetail(false);return;}
      if(!blogResult.ok){setError(blogResult.message);setLoadingDetail(false);return;}
      setListings(inventoryResult.listings);setRelationships(relationshipsResult.relationships);setBlogPosts(blogResult.posts);setError(null);setLoadingDetail(false);
    }).catch((cause:unknown)=>{if(!controller.signal.aborted&&!(cause instanceof DOMException&&cause.name==='AbortError')){setError('No fue posible cargar el perfil profesional.');setLoadingDetail(false);}});
    return()=>controller.abort();
  },[gateway,selectedAgentId]);

  function selectMember(agentId:string):void{
    if(agentId===selectedAgentId)return;
    setLoadingDetail(true);setConversationId(null);setMessages([]);setBlogPosts([]);setBlogEditorOpen(false);setBlogEditingId(null);setSelectedAgentId(agentId);setDraft('');setSection('profile');
  }

  async function handleOpenDm():Promise<void>{
    if(selected===null||selected.isSelf)return;
    setOpeningDm(true);setError(null);
    try{
      const dmResult=selected.directConversationId===null?await gateway.openDm(selected.agentId):{ok:true as const,conversationId:selected.directConversationId};
      if(!dmResult.ok){setError(dmResult.message);return;}
      const messagesResult=await gateway.loadMessages(dmResult.conversationId);
      if(!messagesResult.ok){setError(messagesResult.message);return;}
      setConversationId(dmResult.conversationId);setMessages(messagesResult.messages);
    }finally{setOpeningDm(false);}
  }

  async function handleSend(event:SyntheticEvent<HTMLFormElement>):Promise<void>{
    event.preventDefault();
    if(conversationId===null||draft.trim()==='')return;
    setSending(true);setError(null);
    try{
      const result=await gateway.sendMessage(conversationId,draft.trim());
      if(!result.ok){setError(result.message);return;}
      const refreshed=await gateway.loadMessages(conversationId);
      if(!refreshed.ok){setError(refreshed.message);return;}
      setMessages(refreshed.messages);setDraft('');
    }finally{setSending(false);}
  }

  function openBlogEditor(post:AgencyBlogPost|null):void{
    setBlogEditingId(post?.postId??null);
    setBlogTitle(post?.title??'');
    setBlogSubtitle(post?.subtitle??'');
    setBlogBody(post?.body??'');
    setBlogEditorOpen(true);
  }

  async function saveBlog(publish:boolean):Promise<void>{
    if(selected===null||blogTitle.trim()===''||blogBody.trim()===''||blogSaving)return;
    setBlogSaving(true);setError(null);
    try{
      const result=await gateway.saveBlog(selected.agentId,blogEditingId,blogTitle,blogSubtitle,blogBody,publish);
      if(!result.ok){setError(result.message);return;}
      const refreshed=await gateway.loadBlog(selected.agentId);
      if(!refreshed.ok){setError(refreshed.message);return;}
      setBlogPosts(refreshed.posts);setBlogEditorOpen(false);setBlogEditingId(null);setBlogTitle('');setBlogSubtitle('');setBlogBody('');
    }finally{setBlogSaving(false);}
  }

  return <main className={styles.shell}>
    <header className={styles.topBar}>
      <button type="button" className={styles.brand} onClick={navigateToDashboard}><strong>Alpha•MLS</strong><span>AGENCY</span></button>
      <div className={styles.topActions}><button type="button" onClick={navigateToDashboard}>⌂</button><button type="button" aria-label="Buscar">⌕</button><button type="button" aria-label="Menú">⋮</button></div>
    </header>

    {loading?<div className={styles.loading} role="status">Resolviendo identidad profesional y Agency…</div>:null}
    {error!==null?<div className={styles.error} role="alert">{error}</div>:null}

    {!loading&&identityName!==null&&selected===null?<section className={styles.bootstrap}>
      <div className={styles.bootstrapCover} style={{backgroundImage:`linear-gradient(to top,rgba(4,17,32,.95),rgba(4,17,32,.2)),url(${cover})`}}>
        <button type="button" className={styles.backMobile} onClick={navigateToDashboard}>←</button>
        <button type="button" className={styles.more} aria-label="Más acciones">⋮</button>
        <div className={styles.identity}>
          <span className={styles.avatar}>{initials(identityName)}</span>
          <div><p>AGENCY</p><h1>{identityName}</h1><span>Perfil profesional pendiente de configuración</span></div>
        </div>
      </div>
      <div className={styles.profileActions}>
        <button type="button" disabled>◎ Seguir</button>
        <button type="button" disabled>✈ Mensaje</button>
        <button type="button" disabled>⌂ Ver propiedades</button>
      </div>
      <div className={styles.stats}>
        <article><strong>0</strong><span>Propiedades</span></article>
        <article><strong>0</strong><span>Customers</span></article>
        <article><strong>0</strong><span>Owners</span></article>
        <article><strong>0</strong><span>Conversaciones</span></article>
      </div>
      <nav className={styles.tabs} aria-label="Perfil de Agency">
        <button type="button" aria-pressed="true">Agency</button>
        <button type="button" disabled>Propiedades</button>
        <button type="button" disabled>Blog</button>
      </nav>
      <div className={styles.profileBody}>
        <section className={styles.businessProfile}>
          <p className={styles.eyebrow}>PERFIL PROFESIONAL</p>
          <h2>Configura tu Agency</h2>
          <p>La identidad fundadora está activa. Vincula una Agency y un perfil profesional para habilitar inventario, equipo, Customers, Owners y mensajería.</p>
          <dl>
            <div><dt>Identidad</dt><dd>{identityName}</dd></div>
            <div><dt>Agency</dt><dd>Pendiente de vinculación</dd></div>
            <div><dt>Inventario</dt><dd>Sin propiedades asociadas</dd></div>
            <div><dt>Mensajería</dt><dd>Disponible al existir una identidad profesional</dd></div>
          </dl>
        </section>
        <section className={styles.businessCard}>
          <p className={styles.eyebrow}>BUSINESSCARD</p>
          <h2>Identidad verificable</h2>
          <div className={styles.cardIdentity}><span>{initials(identityName)}</span><div><strong>{identityName}</strong><small>Agency pendiente</small></div></div>
          <button type="button" disabled>QR pendiente</button>
          <small>La tarjeta profesional se activa cuando exista una identidad profesional pública.</small>
        </section>
        <section className={styles.relationships}>
          <div><header><h3>Customers</h3><span>0</span></header><p>Sin relaciones registradas.</p></div>
          <div><header><h3>Owners</h3><span>0</span></header><p>Sin relaciones registradas.</p></div>
        </section>
      </div>
    </section>:null}

    {context!==null&&selected!==null?<div className={styles.workspace}>
      <aside className={styles.teamRail}>
        <header><span>Equipo</span><strong>{context.teamCount}</strong></header>
        {team.map(member=><button key={member.agentId} type="button" className={member.agentId===selected.agentId?styles.memberActive:styles.member} onClick={()=>selectMember(member.agentId)}>
          <span className={styles.miniAvatar}>{initials(member.publicName)}</span>
          <span><strong>{member.publicName}</strong><small>{member.professionalRole??'Profesional'}</small></span>
          {member.isSelf?<em>Tú</em>:null}
        </button>)}
      </aside>

      <section className={styles.profile}>
        <div className={styles.cover} style={{backgroundImage:`linear-gradient(to top,rgba(4,17,32,.94),rgba(4,17,32,.12)),url(${cover})`}}>
          <button type="button" className={styles.backMobile} onClick={navigateToDashboard}>←</button>
          <button type="button" className={styles.more} aria-label="Más acciones">⋮</button>
          <div className={styles.identity}>
            <span className={styles.avatar}>{initials(selected.publicName)}</span>
            <div><p>{context.agencyName}</p><h1>{selected.publicName}</h1><span>{selected.headline??'Profesional inmobiliario'}</span></div>
          </div>
        </div>

        <div className={styles.profileActions}>
          {!selected.isSelf?<button type="button" disabled>◎ Seguir</button>:null}
          {!selected.isSelf?<button type="button" onClick={()=>void handleOpenDm()} disabled={openingDm}>{openingDm?'Abriendo…':'✈ Mensaje'}</button>:null}
          <button type="button" onClick={()=>setSection('inventory')}>⌂ Ver propiedades</button>
        </div>

        <div className={styles.stats}>
          <article><strong>{selected.isSelf?context.activeInventory:listings.length}</strong><span>Propiedades</span></article>
          <article><strong>{customers.length}</strong><span>Customers</span></article>
          <article><strong>{owners.length}</strong><span>Owners</span></article>
          <article><strong>{selected.isSelf?context.visibleConversations:'—'}</strong><span>Conversaciones</span></article>
        </div>

        <nav className={styles.tabs} aria-label="Perfil de Agency">
          <button type="button" aria-pressed={section==='inventory'} onClick={()=>setSection('inventory')}>Propiedades</button>
          <button type="button" aria-pressed={section==='profile'} onClick={()=>setSection('profile')}>Agency</button>
          <button type="button" aria-pressed={section==='blog'} onClick={()=>setSection('blog')}>Blog</button>
        </nav>

        {loadingDetail?<div className={styles.detailLoading} role="status">Leyendo datos autorizados…</div>:null}

        {section==='profile'?<div className={styles.profileBody}>
          <section className={styles.businessProfile}>
            <p className={styles.eyebrow}>PERFIL PROFESIONAL</p>
            <h2>{context.agencyName}</h2>
            <p>{selected.bio??'Perfil profesional dentro del ecosistema Alpha•MLS.'}</p>
            <dl>
              <div><dt>Rol profesional</dt><dd>{selected.professionalRole??context.professionalRole??'Profesional'}</dd></div>
              <div><dt>Registro</dt><dd>{selected.licenseNumber??context.licenseNumber??'Sin folio visible'}</dd></div>
              <div><dt>Ubicación</dt><dd>{selected.city}, {selected.state}</dd></div>
              <div><dt>Correo</dt><dd>{selected.email??context.agentEmail??'Sin dato'}</dd></div>
              <div><dt>Teléfono</dt><dd>{selected.phone??context.agentPhone??'Sin dato'}</dd></div>
            </dl>
            <div className={styles.tags}>{selected.specialties.map(tag=><span key={tag}>{tag}</span>)}</div>
            <p className={styles.executor}>Ejecutor de sesión: <strong>{context.executorDisplayName}</strong></p>
            <p className={styles.executor}>Identidad representada: {selected.publicName}</p>
          </section>

          <section className={styles.businessCard}>
            <p className={styles.eyebrow}>BUSINESSCARD</p><h2>Identidad verificable</h2>
            <div className={styles.cardIdentity}><span>{initials(selected.publicName)}</span><div><strong>{selected.publicName}</strong><small>{context.agencyName}</small></div></div>
            <button type="button" disabled>QR pendiente</button>
            <small>El QR se habilita cuando exista una URL pública congelada.</small>
          </section>

          <section className={styles.relationships}>
            <div><header><h3>Customers</h3><span>{customers.length}</span></header>{customers.slice(0,6).map(item=><article key={item.clientId}><strong>{item.displayName}</strong><small>{item.partyRole}</small></article>)}{customers.length===0?<p>Sin Customers relacionados.</p>:null}</div>
            <div><header><h3>Owners</h3><span>{owners.length}</span></header>{owners.slice(0,6).map(item=><article key={item.clientId}><strong>{item.displayName}</strong><small>{item.partyRole}</small></article>)}{owners.length===0?<p>Sin Owners relacionados.</p>:null}</div>
          </section>
        </div>:null}

        {section==='inventory'?<section className={styles.inventory}>
          <header><div><p className={styles.eyebrow}>INVENTARIO</p><h2>Propiedades de {selected.publicName}</h2></div><strong>{listings.length}</strong></header>
          <div className={styles.inventoryGrid}>{listings.map((listing,index)=><article key={listing.listingId}>
            <div className={styles.propertyPhoto} data-variant={String(index%3)}><span>{listing.transactionType==='sale'?'Venta':'Renta'}</span></div>
            <div><h3>{listing.title}</h3><strong>{money(listing.price,listing.currency)}</strong><small>{listing.propertyType} · {listing.city}, {listing.state}</small></div>
          </article>)}</div>
          {listings.length===0&&!loadingDetail?<p>Sin inventario visible para este perfil.</p>:null}
        </section>:null}

        {section==='blog'?<section className={styles.blog}>
          <header>
            <div><p className={styles.eyebrow}>BLOG</p><h2>Cuaderno profesional de {selected.publicName}</h2><span>Editorial ligero: título, subtítulo y cuerpo. Sin carga multimedia pesada.</span></div>
            {context.canManage?<button type="button" onClick={()=>openBlogEditor(null)}>Nueva entrada</button>:null}
          </header>
          <div className={styles.blogList}>
            {blogPosts.map(post=><article key={post.postId}>
              <small>{post.status==='published'?'PUBLICADO':'BORRADOR'}</small>
              <h3>{post.title}</h3>
              {post.subtitle!==null?<strong>{post.subtitle}</strong>:null}
              <p>{post.body}</p>
              <footer><time>{new Date(post.publishedAt??post.updatedAt).toLocaleDateString('es-MX',{year:'numeric',month:'long',day:'numeric'})}</time>{post.canManage?<button type="button" onClick={()=>openBlogEditor(post)}>Editar</button>:null}</footer>
            </article>)}
            {blogPosts.length===0&&!loadingDetail?<div className={styles.blogEmpty}><strong>Aún no hay publicaciones.</strong><span>La superficie permanece vacía hasta que exista contenido editorial real.</span></div>:null}
          </div>
        </section>:null}

        {blogEditorOpen?<div className={styles.blogEditorBackdrop}>
          <form className={styles.blogEditor} onSubmit={event=>{event.preventDefault();void saveBlog(false);}}>
            <header><div><small>AGENCY · BLOG</small><h2>{blogEditingId===null?'Nueva entrada':'Editar entrada'}</h2></div><button type="button" onClick={()=>setBlogEditorOpen(false)}>×</button></header>
            <label htmlFor="agency-blog-title">Título</label>
            <input id="agency-blog-title" maxLength={180} value={blogTitle} onChange={event=>setBlogTitle(event.currentTarget.value)} />
            <label htmlFor="agency-blog-subtitle">Subtítulo</label>
            <input id="agency-blog-subtitle" maxLength={280} value={blogSubtitle} onChange={event=>setBlogSubtitle(event.currentTarget.value)} />
            <label htmlFor="agency-blog-body">Cuerpo</label>
            <textarea id="agency-blog-body" maxLength={12000} rows={10} value={blogBody} onChange={event=>setBlogBody(event.currentTarget.value)} />
            <footer><button type="submit" disabled={blogSaving||blogTitle.trim()===''||blogBody.trim()===''}>{blogSaving?'Guardando…':'Guardar borrador'}</button><button type="button" disabled={blogSaving||blogTitle.trim()===''||blogBody.trim()===''} onClick={()=>void saveBlog(true)}>Publicar</button></footer>
          </form>
        </div>:null}

        {conversationId!==null?<section className={styles.dm}>
          <header><div><p className={styles.eyebrow}>MENSAJE DIRECTO</p><h2>{selected.publicName}</h2></div><button type="button" onClick={()=>setConversationId(null)}>×</button></header>
          <div className={styles.messages}>{messages.map(message=><article key={message.messageId} className={message.isMine?styles.messageMine:styles.messageOther}><small>{message.senderName}</small><p>{message.body}</p><time>{new Date(message.createdAt).toLocaleTimeString('es-MX',{hour:'2-digit',minute:'2-digit'})}</time></article>)}{messages.length===0?<p>DM creado. Aún no hay mensajes.</p>:null}</div>
          <form className={styles.composer} onSubmit={event=>void handleSend(event)}><label htmlFor="agency-message">Mensaje</label><textarea id="agency-message" value={draft} maxLength={4000} placeholder="Mensaje" onChange={event=>setDraft(event.currentTarget.value)}/><button type="submit" disabled={sending||draft.trim()===''}>{sending?'…':'Enviar'}</button></form>
        </section>:null}
      </section>
    </div>:null}

    <footer className={styles.footer}><span>Alpha•MLS · Agency</span><button type="button" onClick={()=>void gateway.signOut().finally(navigateToAccess)}>Cerrar sesión</button></footer>
  </main>;
}
